package ai.wagemix.logic.mixbuilder

import ai.wagemix.data.create
import ai.wagemix.data.isOwnedBy
import ai.wagemix.data.slice
import ai.wagemix.data.touchesUser
import ai.wagemix.data.WMCache
import ai.wagemix.data.WMJobDescriptionSyncEvent
import ai.wagemix.data.WMMixSyncEvent
import ai.wagemix.data.WMPlanRequiredException
import ai.wagemix.data.WMQuotaExceededException
import ai.wagemix.data.WMRemoteException
import ai.wagemix.data.WMStatsSyncEvent
import ai.wagemix.logic.entitledPlan
import ai.wagemix.logic.isGlobal
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Action
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Event
import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Prompt
import ai.wagemix.logic.durationMsOf
import ai.wagemix.logic.isLapsed
import ai.wagemix.logic.lapsedPlan
import ai.wagemix.logic.retrieveStoredMix
import ai.wagemix.logic.sendWMMixReevaluationCompleted
import ai.wagemix.logic.sendWMMixReevaluationFailed
import ai.wagemix.logic.sendWMMixReevaluationRequested
import ai.wagemix.logic.session
import ai.wagemix.logic.wages.occupationsWithoutData
import ai.wagemix.logic.wages.toWageSummary
import ai.wagemix.logic.wages.WM_DEFAULT_PROJECTED_YEARS
import ai.wagemix.logic.wages.WM_MAX_PROJECTED_YEARS
import ai.wagemix.logic.wages.WMWageSummary
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMPlanRules
import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.withStartedAt
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.fromJobDescription
import ai.wagemix.net.models.isReevaluating
import ai.wagemix.net.models.lastChangedAt
import ai.wagemix.net.models.name
import ai.wagemix.net.models.WMArea
import ai.wagemix.net.models.WMIndustry
import ai.wagemix.net.models.WMStoredMix
import com.fsryan.foundation.data.wasCachedWithin
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPresenter.Event.NullableValueWrapper
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.NullableValueWrapper
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlin.time.Clock
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlin.time.Duration.Companion.hours
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.onEach
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * Which variant of the builder the user sees. [Lapsed] is an account whose subscription ended
 * ([WMPlanRules.isLapsed]): it has Free's controls, and its prompts ask it to renew rather than to upgrade.
 */
enum class WMMixBuilderAccess { LoggedOut, Free, Lapsed, Basic, Pro }

/** The feature a gated control belongs to. */
enum class WMPremiumFeature { Weights, Filters, Save, Share, AiMix }

/**
 * The step of the tutorial a logged-out visitor is shown, in order: add occupations, then the chart's data points
 * (hover), its annual/hourly switch and its projection stepper.
 */
enum class WMMixBuilderTutorialStep { AddOccupations, ChartData, ChartMode, ChartProjection }

data class WMMixOccupationRow(
    val occupationId: Int,
    val title: String,
    /** 0..1, of the occupations that count; 0 for a disabled one or one without data. */
    val share: Double,
    val locked: Boolean,
    val enabled: Boolean,
    /**
     * `false` when the fetched stats have no data for it in the chart's figures (hourly or annual), so it is not part
     * of the mix and the others share its weight; `true` while no stats have arrived.
     */
    val hasData: Boolean
)

data class WMMixDraftPresentation(
    val rows: List<WMMixOccupationRow>,
    val title: String?,
    val sourceMixId: String?,
    /** Differs from the stored mix it was opened from (always `true` for a new, non-empty draft). */
    val dirty: Boolean,
    val areaId: Int?,
    val areaName: String?,
    val industryId: Int?,
    val industryName: String?,
    /** The stored mix it was opened from was generated from a job description, so it can be re-evaluated. */
    val fromJobDescription: Boolean,
    /**
     * An enabled occupation has data, so there are figures to save; `false` for an empty draft and when no enabled
     * occupation has data, which blocks Save and Update. `true` while no stats have arrived.
     */
    val savable: Boolean
) {
    val isEmpty: Boolean get() = rows.isEmpty()
}

data class WMFilterOptions(val areas: List<WMArea>, val industries: List<WMIndustry>)

/**
 * The mix builder. The draft lives in the cache (it survives a reload), stats per request
 * signature live in the local context, and the presenter derives everything else.
 */
interface WMMixBuilderPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class AddOccupation internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<AddOccupation> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RemoveOccupation internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RemoveOccupation> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** [share] is 0..1 of the whole mix. */
        @ConsistentCopyVisibility
        data class SetShare internal constructor(val occupationId: Int, val share: Double, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SetShare> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** The slider was released on [occupationId]; the per-input path is [SetShare]. */
        @ConsistentCopyVisibility
        data class CommitShare internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<CommitShare> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ToggleLock internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ToggleLock> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ToggleEnabled internal constructor(val occupationId: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ToggleEnabled> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class SetFilters internal constructor(val areaId: Int?, val industryId: Int?, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SetFilters> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class SetChartPrefs internal constructor(val hourly: Boolean, val projectedYears: Int, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SetChartPrefs> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestSave internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestSave> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** The save dialog's confirmation, carrying the title the user typed. */
        @ConsistentCopyVisibility
        data class ConfirmSave internal constructor(val title: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ConfirmSave> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestUpdate internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestUpdate> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class RequestDiscard internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestDiscard> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** The visitor finished [step] of the tutorial (its Next or Done button); the next step shows. */
        @ConsistentCopyVisibility
        data class CompleteTutorialStep internal constructor(
            val step: WMMixBuilderTutorialStep,
            override val extendedProperties: Map<String, Any> = emptyMap(),
        ): Base, FSPropertyContainer<CompleteTutorialStep> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** The visitor skipped the tutorial while it showed [step]; it does not come back. */
        @ConsistentCopyVisibility
        data class SkipTutorial internal constructor(
            val step: WMMixBuilderTutorialStep,
            override val extendedProperties: Map<String, Any> = emptyMap(),
        ): Base, FSPropertyContainer<SkipTutorial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Re-run the AI on the job description the draft's stored mix came from. */
        @ConsistentCopyVisibility
        data class RequestReevaluate internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestReevaluate> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** Share the stored mix the draft came from; the plan decides whether the share dialog opens. */
        @ConsistentCopyVisibility
        data class RequestShare internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestShare> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** A locked control was used; the presenter explains why. */
        @ConsistentCopyVisibility
        data class RequestPremium internal constructor(val feature: WMPremiumFeature, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestPremium> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createAddOccupation(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRemoveOccupation(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSetShare(occupationId: Int, share: Double, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createCommitShare(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createToggleLock(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createToggleEnabled(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSetFilters(areaId: Int?, industryId: Int?, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSetChartPrefs(hourly: Boolean, projectedYears: Int, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestSave(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createConfirmSave(title: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestUpdate(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestDiscard(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestPremium(feature: WMPremiumFeature, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestReevaluate(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRequestShare(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createCompleteTutorialStep(
                step: WMMixBuilderTutorialStep,
                extendedProperties: Map<String, Any> = emptyMap(),
            ): A
            fun createSkipTutorial(step: WMMixBuilderTutorialStep, extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base =
                    ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createAddOccupation(occupationId: Int, extendedProperties: Map<String, Any>): Base = AddOccupation(occupationId, extendedProperties)
                override fun createRemoveOccupation(occupationId: Int, extendedProperties: Map<String, Any>): Base = RemoveOccupation(occupationId, extendedProperties)
                override fun createSetShare(occupationId: Int, share: Double, extendedProperties: Map<String, Any>): Base = SetShare(occupationId, share, extendedProperties)
                override fun createCommitShare(occupationId: Int, extendedProperties: Map<String, Any>): Base = CommitShare(occupationId, extendedProperties)
                override fun createToggleLock(occupationId: Int, extendedProperties: Map<String, Any>): Base = ToggleLock(occupationId, extendedProperties)
                override fun createToggleEnabled(occupationId: Int, extendedProperties: Map<String, Any>): Base = ToggleEnabled(occupationId, extendedProperties)
                override fun createSetFilters(areaId: Int?, industryId: Int?, extendedProperties: Map<String, Any>): Base = SetFilters(areaId, industryId, extendedProperties)
                override fun createSetChartPrefs(hourly: Boolean, projectedYears: Int, extendedProperties: Map<String, Any>): Base = SetChartPrefs(hourly, projectedYears, extendedProperties)
                override fun createRequestSave(extendedProperties: Map<String, Any>): Base = RequestSave(extendedProperties)
                override fun createConfirmSave(title: String, extendedProperties: Map<String, Any>): Base = ConfirmSave(title, extendedProperties)
                override fun createRequestUpdate(extendedProperties: Map<String, Any>): Base = RequestUpdate(extendedProperties)
                override fun createRequestDiscard(extendedProperties: Map<String, Any>): Base = RequestDiscard(extendedProperties)
                override fun createRequestPremium(feature: WMPremiumFeature, extendedProperties: Map<String, Any>): Base = RequestPremium(feature, extendedProperties)
                override fun createRequestReevaluate(extendedProperties: Map<String, Any>): Base = RequestReevaluate(extendedProperties)
                override fun createRequestShare(extendedProperties: Map<String, Any>): Base = RequestShare(extendedProperties)
                override fun createCompleteTutorialStep(
                    step: WMMixBuilderTutorialStep,
                    extendedProperties: Map<String, Any>,
                ): Base = CompleteTutorialStep(step, extendedProperties)
                override fun createSkipTutorial(
                    step: WMMixBuilderTutorialStep,
                    extendedProperties: Map<String, Any>,
                ): Base = SkipTutorial(step, extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        val draftUpdate: WMMixDraftPresentation?
        /** The wage summary for the draft, or a wrapped `null` when the draft is empty. */
        val summaryUpdate: NullableValueWrapper<WMWageSummary?>?
        val accessUpdate: WMMixBuilderAccess?
        val chartPrefsUpdate: WMCache.Value.ChartPrefs?
        val filterOptionsUpdate: WMFilterOptions?
        val navigationUpdate: WMNavigationRequest?
        /** Whether the mix the draft came from is being re-evaluated; nothing may change it until it finishes. */
        val reevaluatingUpdate: Boolean?
        /** The tutorial step to show, or a wrapped `null` when there is none (signed in, finished or skipped). */
        val tutorialUpdate: NullableValueWrapper<WMMixBuilderTutorialStep?>?
        /**
         * Whether the API refused the draft's chart because the plan no longer includes its filter (a lapsed account's
         * saved mix, paid-feature plan W4); the chart gives way to a card that offers to renew or clear the filter.
         */
        val chartLockedUpdate: Boolean?
        /** Open the share dialog for this stored mix: the plan includes sharing ([WMPlanRules.canShare]). */
        val shareRequestUpdate: String?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val draftUpdate: WMMixDraftPresentation? = null,
            override val summaryUpdate: NullableValueWrapper<WMWageSummary?>? = null,
            override val accessUpdate: WMMixBuilderAccess? = null,
            override val chartPrefsUpdate: WMCache.Value.ChartPrefs? = null,
            override val filterOptionsUpdate: WMFilterOptions? = null,
            override val navigationUpdate: WMNavigationRequest? = null,
            override val reevaluatingUpdate: Boolean? = null,
            override val tutorialUpdate: NullableValueWrapper<WMMixBuilderTutorialStep?>? = null,
            override val chartLockedUpdate: Boolean? = null,
            override val shareRequestUpdate: String? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createDraftUpdate(draft: WMMixDraftPresentation): E
            fun createSummaryUpdate(summary: WMWageSummary?): E
            fun createAccessUpdate(access: WMMixBuilderAccess): E
            fun createChartPrefsUpdate(prefs: WMCache.Value.ChartPrefs): E
            fun createFilterOptionsUpdate(options: WMFilterOptions): E
            fun createNavigation(request: WMNavigationRequest): E
            fun createReevaluatingUpdate(reevaluating: Boolean): E
            fun createTutorialUpdate(step: WMMixBuilderTutorialStep?): E
            fun createChartLockedUpdate(locked: Boolean): E
            fun createShareRequest(mixId: String): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createDraftUpdate(draft: WMMixDraftPresentation) = Base(base.none(), draftUpdate = draft)
                override fun createSummaryUpdate(summary: WMWageSummary?) = Base(base.none(), summaryUpdate = NullableValueWrapper(summary))
                override fun createAccessUpdate(access: WMMixBuilderAccess) = Base(base.none(), accessUpdate = access)
                override fun createChartPrefsUpdate(prefs: WMCache.Value.ChartPrefs) = Base(base.none(), chartPrefsUpdate = prefs)
                override fun createFilterOptionsUpdate(options: WMFilterOptions) = Base(base.none(), filterOptionsUpdate = options)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
                override fun createReevaluatingUpdate(reevaluating: Boolean) = Base(base.none(), reevaluatingUpdate = reevaluating)
                override fun createTutorialUpdate(step: WMMixBuilderTutorialStep?): Base<P> {
                    return Base(base.none(), tutorialUpdate = NullableValueWrapper(step))
                }
                override fun createChartLockedUpdate(locked: Boolean) = Base(base.none(), chartLockedUpdate = locked)
                override fun createShareRequest(mixId: String) = Base(base.none(), shareRequestUpdate = mixId)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        /** Asks for a title; the dialog answers through `userConfirmedSave(title)` or cancels. */
        @ConsistentCopyVisibility
        data class SaveMix internal constructor(val suggestedTitle: String): Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** The draft was opened from a stored mix: update it in place or save a copy. */
        @ConsistentCopyVisibility
        data class UpdateMix internal constructor(val mixId: String, val title: String): Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel, SaveAsNew, Update }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        @ConsistentCopyVisibility
        data class DiscardDraft internal constructor(val occupationCount: Int): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel, Discard }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** A control the plan does not include. */
        @ConsistentCopyVisibility
        data class PremiumFeature internal constructor(val feature: WMPremiumFeature, val plan: WMSubscriptionType): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, ViewPlans }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /**
         * A control a lapsed account's plan no longer includes: renew rather than upgrade (paid-feature plan W7).
         * [plan] is the plan it lapsed from, the one renewing brings back; `null` when that is not known.
         */
        @ConsistentCopyVisibility
        data class SubscriptionEnded internal constructor(
            val feature: WMPremiumFeature,
            val plan: WMSubscriptionType?,
        ): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, Renew }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** A control that needs an account. */
        @ConsistentCopyVisibility
        data class SignInRequired internal constructor(val feature: WMPremiumFeature): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, SignIn, Register }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** Re-evaluating replaces the stored mix [mixId] (and the draft) with a new AI suggestion from [fileName]. */
        @ConsistentCopyVisibility
        data class ConfirmReevaluate internal constructor(val mixId: String, val fileName: String): Base, FSPrompt.Displayed {
            override val forceInteraction = true
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Cancel, Reevaluate }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun saveMix(suggestedTitle: String): P
            fun updateMix(mixId: String, title: String): P
            fun discardDraft(occupationCount: Int): P
            fun premiumFeature(feature: WMPremiumFeature, plan: WMSubscriptionType): P
            fun subscriptionEnded(feature: WMPremiumFeature, plan: WMSubscriptionType?): P
            fun signInRequired(feature: WMPremiumFeature): P
            fun confirmReevaluate(mixId: String, fileName: String): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun saveMix(suggestedTitle: String): Base = SaveMix(suggestedTitle)
                override fun updateMix(mixId: String, title: String): Base = UpdateMix(mixId, title)
                override fun discardDraft(occupationCount: Int): Base = DiscardDraft(occupationCount)
                override fun premiumFeature(feature: WMPremiumFeature, plan: WMSubscriptionType): Base = PremiumFeature(feature, plan)
                override fun subscriptionEnded(feature: WMPremiumFeature, plan: WMSubscriptionType?): Base {
                    return SubscriptionEnded(feature, plan)
                }
                override fun signInRequired(feature: WMPremiumFeature): Base = SignInRequired(feature)
                override fun confirmReevaluate(mixId: String, fileName: String): Base = ConfirmReevaluate(mixId, fileName)
            }
        }
    }

    fun userAddedOccupation(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userRemovedOccupation(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userChangedShare(occupationId: Int, share: Double, extendedProperties: Map<String, Any> = emptyMap())
    /** The user let go of [occupationId]'s slider; [userChangedShare] already applied every value on the way. */
    fun userCommittedShare(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userToggledLock(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userToggledEnabled(occupationId: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userChangedFilters(areaId: Int?, industryId: Int?, extendedProperties: Map<String, Any> = emptyMap())
    fun userChangedChartPrefs(hourly: Boolean, projectedYears: Int, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedSave(extendedProperties: Map<String, Any> = emptyMap())
    fun userConfirmedSave(title: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedUpdate(extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedDiscard(extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedPremiumFeature(feature: WMPremiumFeature, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedReevaluate(extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedShare(extendedProperties: Map<String, Any> = emptyMap())
    fun userCompletedTutorialStep(step: WMMixBuilderTutorialStep, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedSkipTutorial(step: WMMixBuilderTutorialStep, extendedProperties: Map<String, Any> = emptyMap())

    companion object {
        const val MAX_PROJECTED_YEARS = WM_MAX_PROJECTED_YEARS
        /** Years projected beyond the last actual BLS year until the user picks another number. */
        const val DEFAULT_PROJECTED_YEARS = WM_DEFAULT_PROJECTED_YEARS
    }
}

abstract class WMBaseMixBuilderPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMMixBuilderDataGateway
>(
    /** The stored mix the route opened, or `null` for the current draft. */
    protected val mixId: String?,
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    protected val currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year },
    /** Times the syncs whose duration is reported (re-evaluation). */
    protected val clock: Clock = Clock.System,
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) {
            // The syncs a response starts carry this presenter's id, so its own sync events find their way back,
            // and their start time, so a re-evaluation's success reports its duration.
            val presenterContext = mapOf<String, Any>(FSPresenter.SYNC_EVENT_KEY_INSTANCE_ID to instanceId)
                .withStartedAt(clock)
            defaultMixBuilderHandlePromptResponse(response, presenterContext, emit)
        }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMMixBuilderPresenter<P, E, A> {

    override fun userAddedOccupation(occupationId: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createAddOccupation(occupationId, extendedProperties))
    override fun userRemovedOccupation(occupationId: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRemoveOccupation(occupationId, extendedProperties))
    override fun userChangedShare(occupationId: Int, share: Double, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSetShare(occupationId, share, extendedProperties))
    override fun userCommittedShare(occupationId: Int, extendedProperties: Map<String, Any>) {
        onUserAction(actionFactory.createCommitShare(occupationId, extendedProperties))
    }
    override fun userToggledLock(occupationId: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createToggleLock(occupationId, extendedProperties))
    override fun userToggledEnabled(occupationId: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createToggleEnabled(occupationId, extendedProperties))
    override fun userChangedFilters(areaId: Int?, industryId: Int?, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSetFilters(areaId, industryId, extendedProperties))
    override fun userChangedChartPrefs(hourly: Boolean, projectedYears: Int, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSetChartPrefs(hourly, projectedYears, extendedProperties))
    override fun userRequestedSave(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestSave(extendedProperties))
    override fun userConfirmedSave(title: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createConfirmSave(title, extendedProperties))
    override fun userRequestedUpdate(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestUpdate(extendedProperties))
    override fun userRequestedDiscard(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestDiscard(extendedProperties))
    override fun userRequestedPremiumFeature(feature: WMPremiumFeature, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestPremium(feature, extendedProperties))
    override fun userRequestedReevaluate(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestReevaluate(extendedProperties))
    override fun userRequestedShare(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestShare(extendedProperties))
    override fun userCompletedTutorialStep(step: WMMixBuilderTutorialStep, extendedProperties: Map<String, Any>) {
        onUserAction(actionFactory.createCompleteTutorialStep(step, extendedProperties))
    }
    override fun userRequestedSkipTutorial(step: WMMixBuilderTutorialStep, extendedProperties: Map<String, Any>) {
        onUserAction(actionFactory.createSkipTutorial(step, extendedProperties))
    }

    override fun registerSubscriptions() {
        // Who is signed in changed: the access level follows, and someone else's draft gives way to the default.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .onEach {
                val access = accessOf(dataGateway.session() != null, dataGateway.entitledPlan(), dataGateway.isLapsed())
                eventFactory.createAccessUpdate(access).emit()
                emitTutorial()   // only a logged-out visitor sees it
                seedDefaultDraftIfNeeded(replaceEmpty = false, origin = WMMixOrigin.Default)
            }
            .launchInViewScope()

        // Stats for the current draft arrived (from this or any other presenter's request).
        dataGateway.contextUpdateFlow
            .filter { it.isGlobal }
            .onEach { update ->
                val draft = dataGateway.retrieveOwnMixDraft() ?: return@onEach
                val signature = draft.draftStatsSignature() ?: return@onEach
                if (update.statsChanged(signature)) {
                    emitDraft(draft)   // which occupations have data decides their shares
                    emitSummary(draft)
                }
            }
            .launchInViewScope()

        // A re-evaluation of the open mix started or ended, on this page or another (the upload's status says so).
        dataGateway.contextUpdateFlow
            .filter { it.uploadsChanged() }
            .onEach { emitReevaluating() }
            .launchInViewScope()

        // The stored mix the route opened, or the one being re-evaluated, arrives through the mirror later.
        dataGateway.contextUpdateFlow
            .filter { it.storedMixesChanged() }
            .onEach {
                openRouteMixIfNeeded()
                reloadReevaluatedMix(force = false)
            }
            .launchInViewScope()

        dataGateway.cacheUpdateFlow
            .onEach { update ->
                when (update.slice) {
                    is WMCache.Value.MixDraft -> {
                        // The draft went away while the builder is open (New mix, Discard): a fresh one starts.
                        if (seedDefaultDraftIfNeeded(replaceEmpty = false, origin = WMMixOrigin.NewMix)) {
                            return@onEach   // the seeded draft arrives as its own update
                        }
                        val draft = dataGateway.retrieveOwnMixDraft()
                        emitDraft(draft)
                        requestOrEmitStats(draft)
                        emitReevaluating()   // another mix may have been opened
                        // Launched: the cache waits for every collector, this one included, to take the tutorial
                        // update it emits, so writing it from inside this collector would stall it.
                        launch { completeAddOccupationsIfAdded(draft) }
                    }
                    is WMCache.Value.MixBuilderTutorial -> emitTutorial()
                    is WMCache.Value.ChartPrefs -> {
                        dataGateway.retrieveChartPrefs()?.let { eventFactory.createChartPrefsUpdate(it).emit() }
                        val draft = dataGateway.retrieveOwnMixDraft()
                        emitDraft(draft)   // hourly and annual figures can differ in which occupations have data
                        emitSummary(draft)
                    }
                    is WMCache.Value.Areas, is WMCache.Value.Industries -> emitFilterOptions()
                    is WMCache.Value.Taxonomy -> emitDraft(dataGateway.retrieveOwnMixDraft())
                    else -> Unit
                }
            }
            .launchInViewScope()

        dataGateway.statsSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { handleStatsSyncEvent(it) }
            .launchInViewScope()

        dataGateway.mixSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { handleMixSyncEvent(it) }
            .launchInViewScope()

        dataGateway.jobDescriptionSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { handleJobDescriptionSyncEvent(it) }
            .launchInViewScope()
    }

    private suspend fun handleStatsSyncEvent(event: WMStatsSyncEvent<*>) {
        when (event) {
            is WMStatsSyncEvent.Stats.Initial -> eventFactory.createLoadingChange(true).emit()
            is WMStatsSyncEvent.Stats.Success -> {
                eventFactory.createLoadingChange(false).emit()
                eventFactory.createChartLockedUpdate(false).emit()
                logFilterEmptyResultIfNeeded()
            }
            is WMStatsSyncEvent.Stats.Failure -> {
                val error = event.error
                if (error is WMRemoteException && (error.isPaymentRequired || error.isUnauthorized)) {
                    // The plan does not include the draft's filter: not a network problem (paid-feature plan W4).
                    eventFactory.createLoadingChange(false).emit()
                    eventFactory.createChartLockedUpdate(true).emit()
                } else {
                    eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
                }
            }
            else -> Unit
        }
    }

    private suspend fun handleMixSyncEvent(event: WMMixSyncEvent<*>) {
        when (event) {
            is WMMixSyncEvent.SaveMix.Success -> {
                // The sync wrote the new id as the user's selected mix before it reported success.
                val savedId = dataGateway.retrieveUserContext(event.uid).selectedMixId() ?: return
                logMixSaved(WMMixSaveKind.New, savedId, event.uid)
                dataGateway.modifyOwnMixDraft { it?.withSource(savedId, it.title) }
                eventFactory.createPromptChange(promptFactory.none(), loading = false).emit()
                eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.mix(savedId))).emit()
            }
            is WMMixSyncEvent.UpdateMix.Success -> {
                logMixSaved(WMMixSaveKind.Update, event.mixId, event.uid)
                eventFactory.createPromptChange(promptFactory.none(), loading = false).emit()
                emitDraft(dataGateway.retrieveOwnMixDraft())
            }
            is WMMixSyncEvent.SaveMix.Failure -> {
                eventFactory.createPromptChange(saveFailurePrompt(event.error), loading = false).emit()
            }
            is WMMixSyncEvent.UpdateMix.Failure -> {
                eventFactory.createPromptChange(saveFailurePrompt(event.error), loading = false).emit()
            }
            else -> Unit
        }
    }

    /** The rules refusing a save mean the plan does not include it (it lapsed since the screen opened). */
    private suspend fun saveFailurePrompt(error: Throwable): P = when {
        error !is WMPlanRequiredException -> promptFactory.networkError()
        dataGateway.isLapsed() -> promptFactory.subscriptionEnded(WMPremiumFeature.Save, dataGateway.lapsedPlan())
        else -> promptFactory.premiumFeature(WMPremiumFeature.Save, dataGateway.entitledPlan())
    }

    private suspend fun handleJobDescriptionSyncEvent(event: WMJobDescriptionSyncEvent<*>) {
        when (event) {
            is WMJobDescriptionSyncEvent.Reevaluate.Initial -> eventFactory.createLoadingChange(true).emit()
            is WMJobDescriptionSyncEvent.Reevaluate.Success -> {
                FSLog.sendWMMixReevaluationCompleted(mixId = event.mixId, durationMs = event.durationMsOf(clock))
                eventFactory.createLoadingChange(false).emit()
                reloadReevaluatedMix(force = true)
            }
            is WMJobDescriptionSyncEvent.Reevaluate.Failure -> {
                FSLog.sendWMMixReevaluationFailed(mixId = event.mixId, error = event.error)
                dataGateway.deleteScreenParams(event.uid, WMBaseMixBuilderPresenter.SCREEN)
                emitReevaluating()   // the optimistic lock set when it was requested ends
                val prompt = when (event.error) {
                    is WMQuotaExceededException -> {
                        promptFactory.premiumFeature(WMPremiumFeature.AiMix, dataGateway.entitledPlan())
                    }
                    else -> promptFactory.networkError()
                }
                eventFactory.createPromptChange(prompt, loading = false).emit()
            }
            else -> Unit
        }
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        val access = accessOf(dataGateway.session() != null, dataGateway.entitledPlan(), dataGateway.isLapsed())
        eventFactory.createAccessUpdate(access).emit()
        val prefs = dataGateway.retrieveChartPrefs() ?: defaultChartPrefs()
        eventFactory.createChartPrefsUpdate(prefs).emit()
        if (dataGateway.retrieveTaxonomy()?.wasCachedWithin(REFERENCE_TTL) != true) {
            dataGateway.syncTaxonomy(createPresenterContext())
        }
        // The filter lists do not wait for occupations: an empty set asks for every area and industry with data.
        syncReferenceListsIfStale(dataGateway.retrieveOwnMixDraft())
        emitFilterOptions()
        // A visitor who added occupations before the tutorial existed starts at the chart.
        completeAddOccupationsIfAdded(dataGateway.retrieveOwnMixDraft())
        emitTutorial()

        if (isFirstOnReadyCall && openRouteMixIfNeeded()) {
            return   // the cache update emits the draft and requests stats
        }
        if (isFirstOnReadyCall && seedDefaultDraftIfNeeded(replaceEmpty = true, origin = WMMixOrigin.Default)) {
            return   // the cache update emits the draft
        }
        val draft = dataGateway.retrieveOwnMixDraft()
        emitDraft(draft)
        requestOrEmitStats(draft)
        emitReevaluating()
    }

    /** Whether the mix [draft] came from is being re-evaluated, on this page or any other. */
    private suspend fun isReevaluating(draft: WMCache.Value.MixDraft?): Boolean {
        val mixId = draft?.sourceMixId ?: return false
        val uid = dataGateway.session()?.uid ?: return false
        return dataGateway.retrieveUserContext(uid).uploads()?.isReevaluating(mixId, clock.now()) == true
    }

    /**
     * Says whether the open mix is being re-evaluated. While it is, the screen parameters remember it; when it no
     * longer is, the draft is replaced with the stored mix the re-evaluation rewrote, wherever it was started.
     */
    private suspend fun emitReevaluating() {
        val draft = dataGateway.retrieveOwnMixDraft()
        val reevaluating = isReevaluating(draft)
        eventFactory.createReevaluatingUpdate(reevaluating).emit()
        val uid = dataGateway.session()?.uid ?: return
        val mixId = draft?.sourceMixId ?: return
        val params = dataGateway.retrieveUserContext(uid).screenParams(WMBaseMixBuilderPresenter.SCREEN)
        val wasLocked = params[WMBaseMixBuilderPresenter.PARAM_LOCKED_MIX] == mixId
        when {
            reevaluating && !wasLocked -> {
                val locked = params + (WMBaseMixBuilderPresenter.PARAM_LOCKED_MIX to mixId)
                dataGateway.upsertScreenParams(uid, WMBaseMixBuilderPresenter.SCREEN, locked)
            }
            !reevaluating && wasLocked -> {
                val unlocked = params - WMBaseMixBuilderPresenter.PARAM_LOCKED_MIX
                dataGateway.upsertScreenParams(uid, WMBaseMixBuilderPresenter.SCREEN, unlocked)
                val rewritten = dataGateway.retrieveStoredMix(uid, mixId) ?: return
                dataGateway.upsertMixDraft(rewritten.toDraft(ownerUid = uid))
            }
        }
    }

    /**
     * Opens the stored mix the route named into the draft once the mirror has it and the draft is
     * not already that mix; returns whether it did.
     */
    private suspend fun openRouteMixIfNeeded(): Boolean {
        val routeMixId = mixId ?: return false
        val uid = dataGateway.session()?.uid ?: return false
        if (dataGateway.retrieveOwnMixDraft()?.sourceMixId == routeMixId) {
            return false
        }
        val stored = dataGateway.retrieveStoredMix(uid, routeMixId) ?: return false
        val draft = stored.toDraft(ownerUid = uid)
        dataGateway.upsertMixDraft(draft)
        dataGateway.upsertSelectedMixId(uid, routeMixId)
        val origin = if (stored.fromJobDescription) WMMixOrigin.JobDescription else WMMixOrigin.SavedMix
        FSLog.sendWMMixStarted(origin = origin, occupationCount = draft.occupations.size)
        return true
    }

    /**
     * After a re-evaluation, replaces the draft with the stored mix the pipeline rewrote. The mirror
     * may deliver the rewrite before or after the pipeline reports success, so the screen parameters
     * remember which mix is pending and how it looked before; the reload happens on success ([force])
     * and again when the mirror shows the mix changed, which ends the wait.
     */
    private suspend fun reloadReevaluatedMix(force: Boolean) {
        val uid = dataGateway.session()?.uid ?: return
        val params = dataGateway.retrieveUserContext(uid).screenParams(WMBaseMixBuilderPresenter.SCREEN)
        val pendingId = params[WMBaseMixBuilderPresenter.PARAM_REEVALUATING] ?: return
        val stored = dataGateway.retrieveStoredMix(uid, pendingId) ?: return
        val changed = stored.reevaluationFingerprint() != params[WMBaseMixBuilderPresenter.PARAM_REEVALUATE_BASELINE]
        if (changed) {
            dataGateway.deleteScreenParams(uid, WMBaseMixBuilderPresenter.SCREEN)
        }
        if (changed || force) {
            dataGateway.upsertMixDraft(stored.toDraft(ownerUid = uid))
        }
    }

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        val leavesMixAlone = action is Action.SetChartPrefs || action is Action.RequestPremium
            || action is Action.CompleteTutorialStep || action is Action.SkipTutorial
        if (leavesMixAlone) {
            handleMixBuilderAction(action)
            return
        }
        // Everything else changes, saves or re-runs the mix, which must wait while it is being re-evaluated.
        launch {
            if (isReevaluating(dataGateway.retrieveOwnMixDraft())) {
                eventFactory.createReevaluatingUpdate(true).emit()
                return@launch
            }
            handleMixBuilderAction(action)
        }
    }

    private fun handleMixBuilderAction(action: A) {
        when (action) {
            is Action.AddOccupation -> launch {
                dataGateway.modifyOwnMixDraft { draft ->
                    (draft ?: WMCache.Value.MixDraft.create(emptyList())).adding(action.occupationId)
                }
            }
            is Action.RemoveOccupation -> launch {
                val before = dataGateway.retrieveOwnMixDraft()
                dataGateway.modifyOwnMixDraft { it?.removing(action.occupationId) }
                if (before != null && before.occupations.any { it.id == action.occupationId }) {
                    val remaining = before.removing(action.occupationId).occupations.size
                    FSLog.sendWMOccupationRemoved(occupationCount = remaining)
                }
            }
            is Action.ToggleEnabled -> launch {
                val before = dataGateway.retrieveOwnMixDraft()
                val occupation = before?.occupations?.firstOrNull { it.id == action.occupationId }
                dataGateway.modifyOwnMixDraft { draft ->
                    val current = draft?.occupations?.firstOrNull { it.id == action.occupationId }
                    when {
                        current == null -> draft
                        current.enabled -> draft.disabling(action.occupationId)
                        else -> draft.enabling(action.occupationId)
                    }
                }
                if (before != null && occupation != null) {
                    val count = before.occupations.size
                    FSLog.sendWMOccupationToggled(enabled = !occupation.enabled, occupationCount = count)
                }
            }
            is Action.SetShare -> launch {
                gated(WMPremiumFeature.Weights) {
                    val excluded = dataGateway.occupationsWithoutData(dataGateway.retrieveOwnMixDraft())
                    dataGateway.modifyOwnMixDraft { it?.settingShare(action.occupationId, action.share, excluded) }
                }
            }
            is Action.CommitShare -> launch { handleCommitShare(action) }
            is Action.ToggleLock -> launch {
                gated(WMPremiumFeature.Weights) {
                    val draft = dataGateway.retrieveOwnMixDraft()
                    val occupation = draft?.occupations?.firstOrNull { it.id == action.occupationId }
                    dataGateway.modifyOwnMixDraft { it?.togglingLock(action.occupationId) }
                    occupation?.let { FSLog.sendWMOccupationLocked(locked = !it.locked) }
                }
            }
            is Action.SetFilters -> launch {
                val apply: suspend () -> Unit = {
                    val before = dataGateway.retrieveOwnMixDraft()
                    val (areaId, industryId) = before.exclusiveFilters(action.areaId, action.industryId)
                    dataGateway.modifyOwnMixDraft { draft ->
                        (draft ?: WMCache.Value.MixDraft.create(emptyList())).withFilters(areaId, industryId)
                    }
                    logFilterChanges(before, areaId, industryId)
                }
                // Clearing is always allowed: a lapsed account's saved mix keeps its filter, and clearing it is how
                // that account gets its chart back (paid-feature plan W4, W7).
                if (action.areaId == null && action.industryId == null) {
                    apply()
                } else {
                    gated(WMPremiumFeature.Filters, apply)
                }
            }
            is Action.SetChartPrefs -> launch {
                val before = dataGateway.retrieveChartPrefs() ?: defaultChartPrefs()
                val years = action.projectedYears.coerceIn(0, WMMixBuilderPresenter.MAX_PROJECTED_YEARS)
                dataGateway.upsertChartPrefs(WMCache.Value.ChartPrefs.create(action.hourly, years))
                // Using the control a tutorial step points at completes that step.
                if (before.hourly != action.hourly) {
                    FSLog.sendWMChartModeChanged(hourly = action.hourly)
                    completeTutorialStep(WMMixBuilderTutorialStep.ChartMode, WMTutorialCompletion.Action)
                }
                if (before.projectedYears != years) {
                    FSLog.sendWMProjectionChanged(projectedYears = years)
                    completeTutorialStep(WMMixBuilderTutorialStep.ChartProjection, WMTutorialCompletion.Action)
                }
            }
            is Action.RequestSave -> launch { handleRequestSave() }
            is Action.ConfirmSave -> launch { handleConfirmSave(action) }
            is Action.RequestUpdate -> launch { handleRequestUpdate() }
            is Action.RequestDiscard -> launch {
                val count = dataGateway.retrieveOwnMixDraft()?.occupations?.size ?: 0
                if (count == 0) {
                    return@launch
                }
                eventFactory.createPromptChange(promptFactory.discardDraft(count)).emit()
            }
            is Action.RequestPremium -> launch { gated(action.feature) {} }
            is Action.RequestReevaluate -> launch { handleRequestReevaluate() }
            is Action.RequestShare -> launch {
                // A lapsed account may still have its saved mix open; it gets the renewal prompt, not the dialog (D2).
                val mixId = dataGateway.retrieveOwnMixDraft()?.sourceMixId ?: return@launch
                gated(WMPremiumFeature.Share) { eventFactory.createShareRequest(mixId).emit() }
            }
            is Action.CompleteTutorialStep -> launch { completeTutorialStep(action.step, WMTutorialCompletion.Next) }
            is Action.SkipTutorial -> launch { skipTutorial(action.step) }
            else -> logUnknownActionType(action)
        }
    }

    /** Runs [block] when the plan allows [feature]; otherwise raises the matching prompt. */
    private suspend fun gated(feature: WMPremiumFeature, block: suspend () -> Unit) {
        val session = dataGateway.session()
        val plan = dataGateway.entitledPlan()
        val allowed = when (feature) {
            WMPremiumFeature.Weights -> WMPlanRules.canAdjustWeights(plan)
            WMPremiumFeature.Filters -> WMPlanRules.canFilter(plan)
            WMPremiumFeature.Save -> WMPlanRules.canSave(plan)
            WMPremiumFeature.Share -> WMPlanRules.canShare(plan)
            WMPremiumFeature.AiMix -> WMPlanRules.uploadQuota(plan) != 0
        }
        when {
            session == null -> {
                FSLog.sendWMPremiumGateHit(feature = feature, plan = plan)
                eventFactory.createPromptChange(promptFactory.signInRequired(feature)).emit()
            }
            !allowed -> {
                FSLog.sendWMPremiumGateHit(feature = feature, plan = plan)
                val lapsed = dataGateway.isLapsed()
                val ended = promptFactory.subscriptionEnded(feature, dataGateway.lapsedPlan())
                val prompt = if (lapsed) ended else promptFactory.premiumFeature(feature, plan)
                eventFactory.createPromptChange(prompt).emit()
            }
            else -> block()
        }
    }

    /**
     * The slider was released: the share is already in the draft ([Action.SetShare] on every input),
     * so the commit only reports it. A plan without weights saw its prompt on the first input.
     */
    private suspend fun handleCommitShare(action: Action.CommitShare) {
        if (dataGateway.session() == null) {
            return
        }
        if (!WMPlanRules.canAdjustWeights(dataGateway.entitledPlan())) {
            return
        }
        val draft = dataGateway.retrieveOwnMixDraft() ?: return
        if (draft.occupations.none { it.id == action.occupationId }) {
            return
        }
        FSLog.sendWMOccupationWeightChanged(
            occupationCount = draft.occupations.size,
            lockedCount = draft.occupations.count { it.locked },
        )
    }

    /** One `filter_changed` per filter whose selection differs from the draft's [before] the change. */
    private suspend fun logFilterChanges(before: WMCache.Value.MixDraft?, areaId: Int?, industryId: Int?) {
        if (before?.areaId != areaId) {
            val area = areaId?.let { id -> dataGateway.retrieveAreas()?.data?.firstOrNull { it.id == id } }
            val kind = area?.let { areaKindOf(it.areaType) }
            FSLog.sendWMFilterChanged(filter = WMMixFilterKind.Area, cleared = areaId == null, areaKind = kind)
        }
        if (before?.industryId != industryId) {
            FSLog.sendWMFilterChanged(filter = WMMixFilterKind.Industry, cleared = industryId == null, areaKind = null)
        }
    }

    /** Stats arrived for a filtered draft and the filter leaves nothing to chart: the "no data" notice shows. */
    private suspend fun logFilterEmptyResultIfNeeded() {
        val draft = dataGateway.retrieveOwnMixDraft() ?: return
        val filter = when {
            draft.areaId != null -> WMMixFilterKind.Area
            draft.industryId != null -> WMMixFilterKind.Industry
            else -> return
        }
        val signature = draft.draftStatsSignature() ?: return
        val stats = dataGateway.retrieveGlobalContext().stats(signature) ?: return
        val prefs = dataGateway.retrieveChartPrefs() ?: defaultChartPrefs()
        val summary = stats.toWageSummary(prefs.hourly, prefs.projectedYears, currentYear(), shares = draft.shares())
        if (summary.points.isEmpty()) {
            FSLog.sendWMFilterEmptyResult(filter = filter)
        }
    }

    /** `mix_saved` for the save that just succeeded; the draft still describes what was saved. */
    private suspend fun logMixSaved(kind: WMMixSaveKind, mixId: String, uid: String) {
        val draft = dataGateway.retrieveOwnMixDraft() ?: return
        val source = draft.sourceMixId?.let { dataGateway.retrieveStoredMix(uid, it) }
        FSLog.sendWMMixSaved(
            kind = kind,
            mixId = mixId,
            occupationCount = draft.occupations.size,
            hasFilters = draft.areaId != null || draft.industryId != null,
            fromJobDescription = source?.fromJobDescription == true,
        )
    }

    private suspend fun handleRequestSave() {
        val draft = dataGateway.retrieveOwnMixDraft() ?: return
        if (!dataGateway.hasDataToSave(draft)) {
            return
        }
        gated(WMPremiumFeature.Save) {
            eventFactory.createPromptChange(promptFactory.saveMix(draft.title ?: "")).emit()
        }
    }

    private suspend fun handleConfirmSave(action: Action.ConfirmSave) {
        val draft = dataGateway.retrieveOwnMixDraft() ?: return
        val uid = dataGateway.session()?.uid ?: return
        val title = action.title.trim().ifEmpty { return }
        if (!dataGateway.hasDataToSave(draft)) {
            return
        }
        eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
        dataGateway.saveMix(
            uid = uid,
            title = title,
            occupations = draft.toStoredOccupations(source = null),
            filters = draft.toFilters(),
            additionalContext = createPresenterContext(action)
        )
    }

    private suspend fun handleRequestUpdate() {
        val draft = dataGateway.retrieveOwnMixDraft() ?: return
        val sourceId = draft.sourceMixId ?: return handleRequestSave()
        if (!dataGateway.hasDataToSave(draft)) {
            return
        }
        gated(WMPremiumFeature.Save) {
            eventFactory.createPromptChange(promptFactory.updateMix(sourceId, draft.title ?: "")).emit()
        }
    }

    /**
     * Re-evaluation runs the AI pipeline, so it carries the upload rule (`WMPlanRules.uploadQuota` not 0:
     * Basic and Pro); the quota itself is enforced by the API, whose refusal arrives as a failure.
     */
    private suspend fun handleRequestReevaluate() {
        val sourceId = dataGateway.retrieveOwnMixDraft()?.sourceMixId ?: return
        gated(WMPremiumFeature.AiMix) {
            val uid = dataGateway.session()?.uid ?: return@gated
            val source = dataGateway.retrieveStoredMix(uid, sourceId)
            if (source?.fromJobDescription != true) {
                return@gated
            }
            val upload = dataGateway.retrieveUserContext(uid).uploads()?.firstOrNull { it.mixId == sourceId }
            val fileName = upload?.originalFileName
            eventFactory.createPromptChange(promptFactory.confirmReevaluate(sourceId, fileName ?: "")).emit()
        }
    }

    private suspend fun emitDraft(draft: WMCache.Value.MixDraft?) {
        val taxonomy = dataGateway.retrieveTaxonomy()?.data
        val titles = taxonomy?.let { t -> (t.items + t.root).associate { it.id to it.name } } ?: emptyMap()
        val statsTitles = draft?.draftStatsSignature()?.let { dataGateway.retrieveGlobalContext().stats(it) }
            ?.orderedStats()?.associate { (id, s) -> id to s.title } ?: emptyMap()
        val areas = dataGateway.retrieveAreas()?.data
        val industries = dataGateway.retrieveIndustries()?.data
        val uid = dataGateway.session()?.uid
        val source = draft?.sourceMixId?.let { id -> uid?.let { dataGateway.retrieveStoredMix(it, id) } }
        val excluded = dataGateway.occupationsWithoutData(draft)
        val presentation = WMMixDraftPresentation(
            rows = draft?.occupations?.map { o ->
                WMMixOccupationRow(
                    occupationId = o.id,
                    title = titles[o.id] ?: statsTitles[o.id] ?: "",
                    share = draft.shareOf(o, excluded),
                    locked = o.locked,
                    enabled = o.enabled,
                    hasData = o.id !in excluded
                )
            } ?: emptyList(),
            title = draft?.title,
            sourceMixId = draft?.sourceMixId,
            dirty = draft != null && draft.occupations.isNotEmpty() && (source == null || draft.differsFrom(source)),
            areaId = draft?.areaId,
            areaName = draft?.areaId?.let { id -> areas?.firstOrNull { it.id == id }?.name },
            industryId = draft?.industryId,
            industryName = draft?.industryId?.let { id -> industries?.firstOrNull { it.id == id }?.naicsTitle },
            fromJobDescription = source?.fromJobDescription == true,
            savable = draft != null && draft.occupations.any { it.enabled && it.id !in excluded }
        )
        eventFactory.createDraftUpdate(presentation).emit()
    }

    /**
     * Fetches the draft's stats only when the local context has none for its fetch signature
     * (occupations, area, industry; never weights), otherwise summarises what is there.
     */
    private suspend fun requestOrEmitStats(draft: WMCache.Value.MixDraft?) {
        val signature = draft?.draftStatsSignature()
        if (draft == null || signature == null) {
            eventFactory.createSummaryUpdate(null).emit()
            return
        }
        when (dataGateway.retrieveGlobalContext().stats(signature)) {
            null -> dataGateway.syncStats(createPresenterContext(), draft.statsRequest())
            else -> emitSummary(draft)
        }
        syncReferenceListsIfStale(draft)
    }

    private suspend fun syncReferenceListsIfStale(draft: WMCache.Value.MixDraft?) {
        val ids = draft?.occupations?.map { it.id }?.toSet() ?: emptySet()
        if (dataGateway.retrieveAreas()?.wasCachedWithin(REFERENCE_TTL) != true) {
            dataGateway.syncAreas(ids, createPresenterContext())
        }
        if (dataGateway.retrieveIndustries()?.wasCachedWithin(REFERENCE_TTL) != true) {
            dataGateway.syncIndustries(ids, createPresenterContext())
        }
    }

    /** Summarises the fetched collection with the draft's own shares; no enabled occupation means no summary. */
    private suspend fun emitSummary(draft: WMCache.Value.MixDraft?) {
        val signature = draft?.draftStatsSignature()
        if (signature == null || draft.occupations.none { it.enabled }) {
            eventFactory.createSummaryUpdate(null).emit()
            return
        }
        val stats = dataGateway.retrieveGlobalContext().stats(signature) ?: return
        val prefs = dataGateway.retrieveChartPrefs() ?: defaultChartPrefs()
        val summary = stats.toWageSummary(prefs.hourly, prefs.projectedYears, currentYear(), shares = draft.shares())
        eventFactory.createSummaryUpdate(summary).emit()
    }

    private suspend fun emitFilterOptions() {
        val areas = dataGateway.retrieveAreas()?.data ?: emptyList()
        val industries = dataGateway.retrieveIndustries()?.data ?: emptyList()
        eventFactory.createFilterOptionsUpdate(WMFilterOptions(areas, industries)).emit()
    }

    /**
     * Every user, signed in or not, starts from "All occupations". Seeds when the user has no draft
     * of their own (none at all, or someone else's) and, when [replaceEmpty], also over a new draft
     * with no occupations, which is how the builder opens; while the builder is open, removing the
     * last occupation leaves it empty. Returns whether it seeded; a seed is reported as `mix_started`
     * with [origin].
     */
    private suspend fun seedDefaultDraftIfNeeded(replaceEmpty: Boolean, origin: WMMixOrigin): Boolean {
        val uid = dataGateway.session()?.uid
        val draft = dataGateway.retrieveMixDraft()
        val own = draft?.takeIf { it.isOwnedBy(uid) }
        if (own != null && (!replaceEmpty || own.occupations.isNotEmpty() || own.sourceMixId != null)) {
            return false
        }
        val seeded = WMCache.Value.MixDraft.createDefault(ownerUid = uid)
        dataGateway.upsertMixDraft(seeded)
        FSLog.sendWMMixStarted(origin = origin, occupationCount = seeded.occupations.size)
        return true
    }

    /** The tutorial step a logged-out visitor sees; none once it is finished or skipped, and never when signed in. */
    private suspend fun emitTutorial() {
        val step = when (dataGateway.session()) {
            null -> dataGateway.retrieveMixBuilderTutorial().currentStep()
            else -> null
        }
        eventFactory.createTutorialUpdate(step).emit()
    }

    /** The first step ends when the draft holds an occupation the visitor chose, not just the default one. */
    private suspend fun completeAddOccupationsIfAdded(draft: WMCache.Value.MixDraft?) {
        if (draft == null || draft.occupations.none { it.id != ALL_OCCUPATIONS_ID }) {
            return
        }
        completeTutorialStep(WMMixBuilderTutorialStep.AddOccupations, WMTutorialCompletion.Action)
    }

    /**
     * Moves a logged-out visitor's tutorial from [step] to the next one (or finishes it). Nothing happens unless
     * [step] is the one showing, so a double click or a control used twice moves it on once; the cache update shows
     * the next step.
     */
    private suspend fun completeTutorialStep(step: WMMixBuilderTutorialStep, by: WMTutorialCompletion) {
        if (dataGateway.session() != null || dataGateway.retrieveMixBuilderTutorial().currentStep() != step) {
            return
        }
        dataGateway.modifyMixBuilderTutorial { current ->
            when (current.currentStep()) {
                step -> WMCache.Value.MixBuilderTutorial.create(step.next())
                else -> current
            }
        }
        FSLog.sendWMMixTutorialStepCompleted(step = step, by = by)
    }

    /** Ends a logged-out visitor's tutorial for good while it shows [step]. */
    private suspend fun skipTutorial(step: WMMixBuilderTutorialStep) {
        if (dataGateway.session() != null || dataGateway.retrieveMixBuilderTutorial().currentStep() != step) {
            return
        }
        val finished = WMCache.Value.MixBuilderTutorial.create(WMCache.Value.MixBuilderTutorial.Step.Finished)
        dataGateway.modifyMixBuilderTutorial { finished }
        FSLog.sendWMMixTutorialSkipped(step = step)
    }

    protected open fun accessOf(signedIn: Boolean, plan: WMSubscriptionType?, lapsed: Boolean): WMMixBuilderAccess = when {
        !signedIn -> WMMixBuilderAccess.LoggedOut
        plan == WMSubscriptionType.Pro -> WMMixBuilderAccess.Pro
        plan == WMSubscriptionType.Basic -> WMMixBuilderAccess.Basic
        lapsed -> WMMixBuilderAccess.Lapsed
        else -> WMMixBuilderAccess.Free
    }

    companion object {
        val REFERENCE_TTL = 24.hours
        /** The screen-parameter slot of the builder in the user's context. */
        internal const val SCREEN = "mixBuilder"
        /** The stored mix a re-evaluation is rewriting. */
        internal const val PARAM_REEVALUATING = "reevaluatingMixId"
        /** How that mix looked when the re-evaluation started ([reevaluationFingerprint]). */
        internal const val PARAM_REEVALUATE_BASELINE = "reevaluateBaseline"
        /** The open mix while it is being re-evaluated, so the draft is reloaded when that ends. */
        internal const val PARAM_LOCKED_MIX = "lockedMixId"
    }
}

/** The kind of a BLS area type ("U.S.", "State", "Metropolitan Statistical Area …"); `null` when unknown. */
private fun areaKindOf(areaType: String): WMAreaKind? {
    val type = areaType.lowercase()
    return when {
        type.startsWith("nonmetropolitan") -> WMAreaKind.Nonmetro
        type.startsWith("metropolitan") -> WMAreaKind.Metro
        type.contains("territory") -> WMAreaKind.Territory
        type == "state" -> WMAreaKind.State
        type == "u.s." || type == "national" -> WMAreaKind.National
        else -> null
    }
}

/** The chart preferences before the user chose any. */
/**
 * The draft's occupations the fetched stats have no data for in the chart's figures; none before the stats arrive.
 */
private suspend fun WMMixBuilderDataGateway.occupationsWithoutData(draft: WMCache.Value.MixDraft?): Set<Int> {
    val signature = draft?.draftStatsSignature() ?: return emptySet()
    val stats = retrieveGlobalContext().stats(signature) ?: return emptySet()
    val hourly = (retrieveChartPrefs() ?: defaultChartPrefs()).hourly
    return stats.occupationsWithoutData(draft.occupations.map { it.id }, hourly)
}

/** A mix is saved only with figures in it: an enabled occupation the fetched stats have data for. */
private suspend fun WMMixBuilderDataGateway.hasDataToSave(draft: WMCache.Value.MixDraft): Boolean {
    val excluded = occupationsWithoutData(draft)
    return draft.occupations.any { it.enabled && it.id !in excluded }
}

private fun defaultChartPrefs(): WMCache.Value.ChartPrefs {
    val years = WMMixBuilderPresenter.DEFAULT_PROJECTED_YEARS
    return WMCache.Value.ChartPrefs.create(hourly = false, projectedYears = years)
}

/** The step this progress shows; a device that never moved on starts at the first one, a finished one shows none. */
private fun WMCache.Value.MixBuilderTutorial?.currentStep(): WMMixBuilderTutorialStep? {
    return when (this?.step ?: WMCache.Value.MixBuilderTutorial.Step.AddOccupations) {
        WMCache.Value.MixBuilderTutorial.Step.AddOccupations -> WMMixBuilderTutorialStep.AddOccupations
        WMCache.Value.MixBuilderTutorial.Step.ChartData -> WMMixBuilderTutorialStep.ChartData
        WMCache.Value.MixBuilderTutorial.Step.ChartMode -> WMMixBuilderTutorialStep.ChartMode
        WMCache.Value.MixBuilderTutorial.Step.ChartProjection -> WMMixBuilderTutorialStep.ChartProjection
        WMCache.Value.MixBuilderTutorial.Step.Finished -> null
    }
}

/** The stored step after [this] one is completed. */
private fun WMMixBuilderTutorialStep.next(): WMCache.Value.MixBuilderTutorial.Step = when (this) {
    WMMixBuilderTutorialStep.AddOccupations -> WMCache.Value.MixBuilderTutorial.Step.ChartData
    WMMixBuilderTutorialStep.ChartData -> WMCache.Value.MixBuilderTutorial.Step.ChartMode
    WMMixBuilderTutorialStep.ChartMode -> WMCache.Value.MixBuilderTutorial.Step.ChartProjection
    WMMixBuilderTutorialStep.ChartProjection -> WMCache.Value.MixBuilderTutorial.Step.Finished
}

/** What a re-evaluation changes about a stored mix: when it was written and what it holds. */
internal fun WMStoredMix.reevaluationFingerprint(): String {
    val holds = occupations.joinToString(",") { "${it.id}:${it.percentage}:${it.isEnabled}" }
    return "${lastChangedAt?.toEpochMilliseconds() ?: ""}|$holds"
}

/**
 * The default prompt handling: typed selections, foundation prompts fall through. [presenterContext]
 * is attached to every sync a response starts, so the presenter's own sync events reach it.
 */
@OptIn(ExperimentalUuidApi::class)
context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMMixBuilderDataGateway
> defaultMixBuilderHandlePromptResponse(
    response: FSPrompt.Response<P>,
    presenterContext: Map<String, Any>,
    emit: suspend E.() -> Unit
) {
    when (val prompt = response.prompt) {
        is Prompt.SaveMix -> eventFactory.createPromptChange(promptFactory.none()).emit()
        is Prompt.UpdateMix -> {
            when (prompt.selectionOf(response.selected)) {
                Prompt.UpdateMix.Selection.Update -> {
                    val draft = dataGateway.retrieveOwnMixDraft()
                    val uid = dataGateway.session()?.uid
                    if (draft != null && uid != null && dataGateway.hasDataToSave(draft)) {
                        val source = dataGateway.retrieveStoredMix(uid, prompt.mixId)
                        eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
                        dataGateway.updateMix(
                            uid = uid,
                            mixId = prompt.mixId,
                            title = draft.title ?: prompt.title,
                            occupations = draft.toStoredOccupations(source),
                            filters = draft.toFilters(),
                            additionalContext = presenterContext
                        )
                        return
                    }
                    eventFactory.createPromptChange(promptFactory.none()).emit()
                }
                Prompt.UpdateMix.Selection.SaveAsNew -> eventFactory.createPromptChange(promptFactory.saveMix("${prompt.title} (copy)")).emit()
                Prompt.UpdateMix.Selection.Cancel, null -> eventFactory.createPromptChange(promptFactory.none()).emit()
            }
        }
        is Prompt.DiscardDraft -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            if (prompt.selectionOf(response.selected) == Prompt.DiscardDraft.Selection.Discard) {
                val uid = dataGateway.session()?.uid
                val sourceMixId = dataGateway.retrieveOwnMixDraft()?.sourceMixId
                FSLog.sendWMMixDiscarded(mixId = sourceMixId)
                val stored = if (uid != null && sourceMixId != null) dataGateway.retrieveStoredMix(uid, sourceMixId) else null
                if (stored != null) {
                    // Discarding changes to a saved mix puts the saved mix back; the page stays on it.
                    dataGateway.upsertMixDraft(stored.toDraft(ownerUid = uid))
                } else {
                    // Nothing saved to go back to (the mix was deleted meanwhile): start a new mix.
                    dataGateway.deleteMixDraft()
                    dataGateway.deleteSelectedMixId(uid)
                    eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.MIX)).emit()
                }
            }
        }
        is Prompt.PremiumFeature -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            if (prompt.selectionOf(response.selected) == Prompt.PremiumFeature.Selection.ViewPlans) {
                eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.PRICING)).emit()
            }
        }
        is Prompt.SubscriptionEnded -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            if (prompt.selectionOf(response.selected) == Prompt.SubscriptionEnded.Selection.Renew) {
                eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.PRICING)).emit()
            }
        }
        is Prompt.SignInRequired -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
            when (prompt.selectionOf(response.selected)) {
                Prompt.SignInRequired.Selection.SignIn -> eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.LOGIN)).emit()
                Prompt.SignInRequired.Selection.Register -> eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.REGISTER)).emit()
                else -> Unit
            }
        }
        is Prompt.ConfirmReevaluate -> {
            val uid = dataGateway.session()?.uid
            if (prompt.selectionOf(response.selected) != Prompt.ConfirmReevaluate.Selection.Reevaluate || uid == null) {
                eventFactory.createPromptChange(promptFactory.none()).emit()
                return
            }
            val baseline = dataGateway.retrieveStoredMix(uid, prompt.mixId)?.reevaluationFingerprint() ?: ""
            val params = mapOf(
                WMBaseMixBuilderPresenter.PARAM_REEVALUATING to prompt.mixId,
                WMBaseMixBuilderPresenter.PARAM_REEVALUATE_BASELINE to baseline,
            )
            dataGateway.upsertScreenParams(uid, WMBaseMixBuilderPresenter.SCREEN, params)
            eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
            // Locked at once; the upload's status keeps it locked from when the API takes the request until it ends.
            eventFactory.createReevaluatingUpdate(true).emit()
            FSLog.sendWMMixReevaluationRequested(mixId = prompt.mixId)
            dataGateway.reevaluateMix(uid, Uuid.random().toString(), prompt.mixId, presenterContext)
        }
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultMixBuilderPresenter = WMMixBuilderPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMMixBuilderPresenter.Companion.createDefault(
    mixId: String?,
    dataGateway: WMMixBuilderDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMMixBuilderPresenter"),
    currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year },
    clock: Clock = Clock.System
): WMDefaultMixBuilderPresenter = object: WMBaseMixBuilderPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMMixBuilderDataGateway
>(
    mixId = mixId,
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId,
    currentYear = currentYear,
    clock = clock
) {}
